import { useEffect, useMemo, useState } from 'react';
import { Api } from '@infra/api';
import { Analyses, AnalysesNorms, Group, Units, AnalysisContext, AnalysesFull, Specialization } from '@domain/models';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { SearchSelect } from '../components/SearchSelect';

const AGE_UNIT_LABELS: Record<string, string> = { days: 'дней', months: 'мес', years: 'год' };
const ageUnitLabel = (unit?: string | null) => AGE_UNIT_LABELS[unit || 'years'] || 'год';
const genderLabel = (gender?: string | null) => gender === 'any' ? 'Любой' : gender || '—';
const groupOptionLabel = (group: Group) =>
  `${group.groups_name} (${genderLabel(group.gender)}) ${group.groups_min_age} ${ageUnitLabel(group.groups_min_age_unit)} – ${group.groups_max_age} ${ageUnitLabel(group.groups_max_age_unit)}`;
const normGroupLabel = (group?: AnalysesFull['norms'][number]['group']) =>
  group?.name ? `${group.name} (${genderLabel(group.gender)})` : '—';

const empty: AnalysesNorms = {
  analysis_id: 0,
  norm_name: '',
  norm_code: '',
  norm_description: '',
  norm_type: 'numeric',
  numeric_min: null,
  numeric_max: null,
  serious_lte: null,
  serious_gte: null,
  norm_unit: null,
};

export function NormsPage() {
  const [analyses, setAnalyses] = useState<Analyses[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [units, setUnits] = useState<Units[]>([]);
  const [contexts, setContexts] = useState<AnalysisContext[]>([]);
  const [specializations, setSpecializations] = useState<Specialization[]>([]);
  const [form, setForm] = useState<AnalysesNorms>(() => {
    const saved = localStorage.getItem('norms_page_analysis_id');
    const analysisId = saved ? Number(saved) : 0;
    return { ...empty, analysis_id: analysisId };
  });
  const [analysisFull, setAnalysisFull] = useState<AnalysesFull | null>(null);
  const [loading, setLoading] = useState(false);
  const [edit, setEdit] = useState<AnalysesNorms & { id?: number } | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [editNormId, setEditNormId] = useState<number | null>(null);
  const [synonyms, setSynonyms] = useState<Array<{ id?: number; analyses_norm_id: number; value_text: string; normalized_code: string; is_allowed?: boolean }>>([]);
  const [newSynonym, setNewSynonym] = useState<{ value_text: string; normalized_code: string; is_allowed: boolean }>({ value_text: '', normalized_code: '', is_allowed: true });
  const [pendingSynonyms, setPendingSynonyms] = useState<Array<{ value_text: string; normalized_code: string; is_allowed: boolean }>>([]);
  const [editingSynonymId, setEditingSynonymId] = useState<number | null>(null);
  const [editingSynonym, setEditingSynonym] = useState<{ id: number; analyses_norm_id: number; value_text: string; normalized_code: string; is_allowed: boolean } | null>(null);
  const [confirmDeleteSynonymId, setConfirmDeleteSynonymId] = useState<number | null>(null);

  useEffect(() => {
    Promise.all([
      Api.listAnalyses(),
      Api.listGroups(),
      Api.listUnits(),
      Api.listContexts(),
      Api.listSpecializations(),
    ]).then(([a, g, u, c, s]) => {
      setAnalyses(a); setGroups(g); setUnits(u); setContexts(c); setSpecializations(s);
      const savedAnalysisId = localStorage.getItem('norms_page_analysis_id');
      const analysisId = savedAnalysisId ? Number(savedAnalysisId) : (a[0]?.id ?? 0);
      const validAnalysisId = a.find(a => a.id === analysisId) ? analysisId : (a[0]?.id ?? 0);
      if (validAnalysisId > 0) {
        setForm(f => ({ ...f, analysis_id: validAnalysisId }));
      }
    }).catch(console.error);
  }, []);

  useEffect(() => {
    if (form.analysis_id && form.analysis_id > 0) {
      setLoading(true);
      Api.getAnalysesFullById(form.analysis_id)
        .then((full) => {
          if (full) {
            setAnalysisFull(full);
          }
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setAnalysisFull(null);
    }
  }, [form.analysis_id]);

  useEffect(() => {
    async function loadNormData() {
      if (!edit) return;
      
      const currentNormId = (edit as any).id as number | undefined;
      if (!currentNormId) return;
      
      const analysisId = edit.analysis_id ?? form.analysis_id;
      if (!analysisId || analysisId === 0) return;
      
      try {
        const full = await Api.getAnalysesFullById(analysisId);
        if (full) {
          const updatedNorm = full.norms.find(n => n.id === currentNormId);
          if (updatedNorm) {
            setEdit({...updatedNorm as any, analysis_id: analysisId});
            setAnalysisFull(full);
          }
        }
      } catch (error) {
        console.error('Ошибка при загрузке данных нормы:', error);
      }
    }
    loadNormData();
  }, [edit ? (edit as any).id : null]);

  useEffect(() => {
    async function loadSynonyms() {
      if (!edit) {
        setEditNormId(null);
        setSynonyms([]);
        return;
      }
      
      const currentNormId = (edit as any).id as number | undefined;
      if (!currentNormId) {
        setEditNormId(null);
        setSynonyms([]);
        return;
      }
      
      setEditNormId(currentNormId);
      
      try {
        const list = await Api.listTextRulesByNorm(currentNormId);
        setSynonyms(list as any);
      } catch (error) {
        console.error('Ошибка при загрузке синонимов:', error);
        setSynonyms([]);
      }
    }
    loadSynonyms();
  }, [edit ? (edit as any).id : null]);

  const selectedAnalysis = useMemo(() => analyses.find(a => (a.id ?? 0) === form.analysis_id), [analyses, form.analysis_id]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    const created = await Api.createNorm(form);

    if (form.norm_type === 'text' && pendingSynonyms.length > 0) {
      try {
        const normId = (created as any)?.id as number | undefined;
          if (normId) {
            for (const s of pendingSynonyms) {
              await Api.createTextRule({ analyses_norm_id: normId, ...s });
            }
        } else {
          console.error('Failed to get norm id from created norm response');
        }
      } catch (err) {
        console.error('Failed to create synonyms for new text norm', err);
      }
    }

    if (form.analysis_id && form.analysis_id > 0) {
      try {
        const full = await Api.getAnalysesFullById(form.analysis_id);
        if (full) {
          setAnalysisFull(full);
        }
      } catch (err) {
        console.error('Failed to reload analysis data', err);
      }
    }

    setPendingSynonyms([]);
    setForm({ ...empty, analysis_id: form.analysis_id });
  }

  return (
    <div>
      <div className="toolbar">
        <div className="title"><h2 style={{margin:0}}>Параметры</h2></div>
      </div>
      <div className="row">
        <form className="card" onSubmit={submit} style={{maxWidth:720}}>
          <h3 style={{marginTop:0}}>Новый параметр</h3>
          <label>Анализ</label>
          <SearchSelect
            options={analyses.map(a=>({ value: a.id ?? 0, label: a.analyses_name }))}
            value={form.analysis_id}
            onChange={(v)=>{
              const analysisId = Number(v);
              setForm({...form, analysis_id: analysisId});
              localStorage.setItem('norms_page_analysis_id', String(analysisId));
            }}
          />
          <label>Тип значения</label>
          <select value={form.norm_type} onChange={e=>setForm({...form, norm_type:e.target.value})}>
            <option value="numeric">numeric</option>
            <option value="text">conclusion</option>
          </select>

          {form.norm_type !== 'text' && (
            <div className="row">
              <div>
                <label>Параметр</label>
                <input value={form.norm_name} onChange={e=>setForm({...form, norm_name:e.target.value})} required />
              </div>
              <div>
                <label>Международное название</label>
                <input value={form.norm_code} onChange={e=>setForm({...form, norm_code:e.target.value})} required />
              </div>
            </div>
          )}

          {form.norm_type === 'text' && (
            <div>
              <label>Заключение</label>
              <input value={form.norm_name} onChange={e=>setForm({...form, norm_name:e.target.value})} required />
            </div>
          )}

          {form.norm_type === 'text' && (
            <div className="row">
              <div>
                <label>Группа</label>
                <SearchSelect
                  options={[{value: 0 as any, label: '—'}, ...groups.map(g=>({ value: g.id ?? 0, label: groupOptionLabel(g) }))]}
                  value={(form.group_id ?? 0) as any}
                  onChange={(v)=>setForm({...form, group_id: v ? Number(v) : null})}
                />
              </div>
              <div>
                <label>Ед. измерения</label>
                <SearchSelect
                  options={[{value: 0 as any, label: '—'}, ...units.map(u=>({ value: u.id ?? 0, label: `${u.unit_name} (${u.unit_code})` }))]}
                  value={(form.unit_id ?? 0) as any}
                  onChange={(v)=>setForm({...form, unit_id: v ? Number(v) : null, norm_unit: v ? (units.find(u=>u.id==Number(v))?.unit_name || null) : null})}
                />
              </div>
              <div>
                <label>Контекст</label>
                <SearchSelect
                  options={[{value: 0 as any, label: '—'}, ...contexts.map(c=>({ value: c.id ?? 0, label: c.context_name }))]}
                  value={(form.context_id ?? 0) as any}
                  onChange={(v)=>setForm({...form, context_id: v ? Number(v) : null})}
                />
              </div>
              <div>
                <label>Специализация</label>
                <SearchSelect
                  options={[{value: 0 as any, label: '—'}, ...specializations.map(s=>({ value: s.id ?? 0, label: s.name }))]}
                  value={(form.specialization_id ?? 0) as any}
                  onChange={(v)=>setForm({...form, specialization_id: v ? Number(v) : null})}
                />
              </div>
            </div>
          )}

          {form.norm_type === 'numeric' ? (
            <div className="row">
              <div>
                <label>Норма: мин</label>
                <input type="number" value={form.numeric_min ?? ''} onChange={e=>setForm({...form, numeric_min:e.target.value===''? null : Number(e.target.value)})} />
              </div>
              <div>
                <label>Норма: макс</label>
                <input type="number" value={form.numeric_max ?? ''} onChange={e=>setForm({...form, numeric_max:e.target.value===''? null : Number(e.target.value)})} />
              </div>
              <div>
                <label>Серьёзное: ≤</label>
                <input type="number" value={form.serious_lte ?? ''} onChange={e=>setForm({...form, serious_lte:e.target.value===''? null : Number(e.target.value)})} />
              </div>
              <div>
                <label>Серьёзное: ≥</label>
                <input type="number" value={form.serious_gte ?? ''} onChange={e=>setForm({...form, serious_gte:e.target.value===''? null : Number(e.target.value)})} />
              </div>
            </div>
          ) : (
            <div>
              <h4>Синонимы (текстовые значения)</h4>
              <div className="row">
                <div>
                  <label>{form.norm_type === 'text' ? 'Ключевые слова нормы' : 'Текст'}</label>
                  <input value={newSynonym.value_text} onChange={e=>setNewSynonym({...newSynonym, value_text:e.target.value})} />
                </div>
                <div>
                  <label>{form.norm_type === 'text' ? 'Ключевые слова отклонений' : 'Норм. код'}</label>
                  <input value={newSynonym.normalized_code} onChange={e=>setNewSynonym({...newSynonym, normalized_code:e.target.value})} />
                </div>
                <div>
                  <label>Допустимо</label>
                  <select value={String(newSynonym.is_allowed)} onChange={e=>setNewSynonym({...newSynonym, is_allowed: e.target.value==='true'})}>
                    <option value="true">true</option>
                    <option value="false">false</option>
                  </select>
                </div>
              </div>
              <div style={{marginTop:12, display:'flex', gap:8}}>
                <button type="button" onClick={()=>{
                  if (!newSynonym.value_text || !newSynonym.normalized_code) return;
                  setPendingSynonyms([{ ...newSynonym }, ...pendingSynonyms]);
                  setNewSynonym({ value_text:'', normalized_code:'', is_allowed: true });
                }}>Добавить синоним</button>
                {pendingSynonyms.length>0 && <span className="badge">Черновиков: {pendingSynonyms.length}</span>}
              </div>
              {pendingSynonyms.length>0 && (
                <table className="table" style={{marginTop:12}}>
                  <thead>
                    <tr><th>{form.norm_type === 'text' ? 'Ключевые слова нормы' : 'Текст'}</th><th>{form.norm_type === 'text' ? 'Ключевые слова отклонений' : 'Код'}</th><th>Допустимо</th><th></th></tr>
                  </thead>
                  <tbody>
                    {pendingSynonyms.map((s, idx) => (
                      <tr key={`${s.value_text}-${s.normalized_code}-${idx}`}>
                        <td>{s.value_text}</td>
                        <td>{s.normalized_code}</td>
                        <td>{String(s.is_allowed)}</td>
                        <td>
                          <button type="button" className="secondary" onClick={()=>{
                            setPendingSynonyms(pendingSynonyms.filter((_, i)=> i!==idx));
                          }}>Убрать</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <p className="muted">Синонимы сохранятся вместе с новой нормой.</p>
            </div>
          )}

          {form.norm_type !== 'text' && (
            <div className="row">
              <div>
                <label>Группа</label>
                <SearchSelect
                  options={[{value: 0 as any, label: '—'}, ...groups.map(g=>({ value: g.id ?? 0, label: groupOptionLabel(g) }))]}
                  value={(form.group_id ?? 0) as any}
                  onChange={(v)=>setForm({...form, group_id: v ? Number(v) : null})}
                />
              </div>
              <div>
                <label>Ед. измерения</label>
                <SearchSelect
                  options={[{value: 0 as any, label: '—'}, ...units.map(u=>({ value: u.id ?? 0, label: `${u.unit_name} (${u.unit_code})` }))]}
                  value={(form.unit_id ?? 0) as any}
                  onChange={(v)=>setForm({...form, unit_id: v ? Number(v) : null, norm_unit: v ? (units.find(u=>u.id==Number(v))?.unit_name || null) : null})}
                />
              </div>
              <div>
                <label>Контекст</label>
                <SearchSelect
                  options={[{value: 0 as any, label: '—'}, ...contexts.map(c=>({ value: c.id ?? 0, label: c.context_name }))]}
                  value={(form.context_id ?? 0) as any}
                  onChange={(v)=>setForm({...form, context_id: v ? Number(v) : null})}
                />
              </div>
              <div>
                <label>Специализация</label>
                <SearchSelect
                  options={[{value: 0 as any, label: '—'}, ...specializations.map(s=>({ value: s.id ?? 0, label: s.name }))]}
                  value={(form.specialization_id ?? 0) as any}
                  onChange={(v)=>setForm({...form, specialization_id: v ? Number(v) : null})}
                />
              </div>
            </div>
          )}

          {form.norm_type !== 'text' && (
            <>
              <label>Описание</label>
              <input value={form.norm_description} onChange={e=>setForm({...form, norm_description:e.target.value})} required />
            </>
          )}

          <div style={{marginTop:12}}>
            <button>Создать</button>
          </div>
          {selectedAnalysis && <p className="badge">{selectedAnalysis.analyses_name}</p>}
        </form>

      </div>
      <div className="card" style={{marginTop:16}}>
        <div className="toolbar">
          <h3 style={{margin:0}}>Параметры анализа</h3>
          {loading && <span style={{color:'var(--muted)'}}>Загрузка...</span>}
        </div>
        {analysisFull ? (
          <>
            <div style={{marginBottom: 12, padding: '8px 12px', background: 'var(--input-bg)', borderRadius: 8}}>
              <strong>{analysisFull.analyses_name}</strong>
              {analysisFull.analyses_description && <div style={{fontSize: '0.9em', color: 'var(--muted)', marginTop: 4}}>{analysisFull.analyses_description}</div>}
        </div>
        <table className="table">
          <thead>
                <tr><th>Параметр</th><th>Международное название</th><th>Тип</th><th>Диапазон</th><th>Серьёзные</th><th>Ед.</th><th>Группа/пол</th><th>Контекст</th><th>Спец.</th><th>Синонимы</th></tr>
          </thead>
          <tbody>
                {analysisFull.norms.map((n) => {
                  const uniqueKey = `norm-${n.id}`;
                  
                  return (
                    <tr key={uniqueKey} style={{cursor:'pointer'}} onClick={()=>setEdit({...n as any, analysis_id: form.analysis_id})}>
                      <td>{n.norm_name || '—'}</td>
                      <td>{n.norm_code || '—'}</td>
                <td>{n.norm_type}</td>
                      <td>{n.norm_type === 'numeric' ? `${n.numeric_min ?? ''}–${n.numeric_max ?? ''}` : '—'}</td>
                      <td>{n.norm_type === 'numeric' ? `${n.serious_lte!=null? `≤${n.serious_lte}`:''} ${n.serious_gte!=null? `≥${n.serious_gte}`:''}`.trim() || '—' : '—'}</td>
                      <td>{n.unit?.unit_name ?? n.norm_unit ?? '—'}</td>
                      <td>{normGroupLabel(n.group)}</td>
                      <td>{n.context?.context_name ?? '—'}</td>
                      <td>{n.specialization?.name ?? '—'}</td>
                      <td>
                        {n.norm_type === 'text' && n.text_rules && n.text_rules.length > 0 ? (
                          <div style={{maxWidth: 200, fontSize: '0.85em'}}>
                            {n.text_rules.slice(0, 2).map((r, idx) => (
                              <div key={idx} style={{marginBottom: 2}}>
                                {r.value_text}→{r.normalized_code} {r.is_allowed ? '✓' : '✗'}
                              </div>
                            ))}
                            {n.text_rules.length > 2 && <div style={{color: 'var(--muted)'}}>+{n.text_rules.length - 2} ещё</div>}
                          </div>
                        ) : n.norm_type === 'text' ? (
                          <span style={{color: 'var(--muted)', fontStyle: 'italic'}}>Нет синонимов</span>
                        ) : (
                          '—'
                        )}
                      </td>
              </tr>
                  );
                })}
          </tbody>
        </table>
            {analysisFull.norms.length === 0 && (
              <p style={{color: 'var(--muted)', fontStyle: 'italic', padding: '20px', textAlign: 'center'}}>
                Нормы для этого анализа не найдены
              </p>
            )}
          </>
        ) : (
          <p style={{color: 'var(--muted)', fontStyle: 'italic', padding: '20px', textAlign: 'center'}}>
            Выберите анализ в форме создания параметра для просмотра его параметров
          </p>
        )}
      </div>

      {edit && (
        <Modal title={`Норма: ${edit.norm_code || edit.norm_name || 'Редактирование'}`} onClose={()=>{
          setEdit(null);
          setEditNormId(null);
          setSynonyms([]);
          setEditingSynonymId(null);
          setEditingSynonym(null);
          setConfirmDeleteSynonymId(null);
        }}>
          {/* Редактирование основных параметров */}
          <div className="row">
            <div>
              <label>{edit.norm_type === 'text' ? 'Заключение' : 'Параметр'}</label>
              <input value={edit.norm_name || ''} onChange={e=>setEdit({...edit!, norm_name:e.target.value})} required />
            </div>
            {edit.norm_type !== 'text' && (
              <div>
                <label>Международное название</label>
                <input value={edit.norm_code || ''} onChange={e=>setEdit({...edit!, norm_code:e.target.value})} required />
              </div>
            )}
          </div>

          {edit.norm_type !== 'text' && (
            <>
              <div className="row">
                <div>
                  <label>Группа</label>
                  <SearchSelect
                    options={[{value: 0 as any, label: '—'}, ...groups.map(g=>({ value: g.id ?? 0, label: groupOptionLabel(g) }))]}
                    value={(edit.group_id ?? 0) as any}
                    onChange={(v)=>setEdit({...edit!, group_id: v ? Number(v) : null})}
                  />
                </div>
                <div>
                  <label>Ед. измерения</label>
                  <SearchSelect
                    options={[{value: 0 as any, label: '—'}, ...units.map(u=>({ value: u.id ?? 0, label: `${u.unit_name} (${u.unit_code})` }))]}
                    value={(edit.unit_id ?? 0) as any}
                    onChange={(v)=>setEdit({...edit!, unit_id: v ? Number(v) : null, norm_unit: v ? (units.find(u=>u.id==Number(v))?.unit_name || null) : null})}
                  />
                </div>
                <div>
                  <label>Контекст</label>
                  <SearchSelect
                    options={[{value: 0 as any, label: '—'}, ...contexts.map(c=>({ value: c.id ?? 0, label: c.context_name }))]}
                    value={(edit.context_id ?? 0) as any}
                    onChange={(v)=>setEdit({...edit!, context_id: v ? Number(v) : null})}
                  />
                </div>
                <div>
                  <label>Специализация</label>
                  <SearchSelect
                    options={[{value: 0 as any, label: '—'}, ...specializations.map(s=>({ value: s.id ?? 0, label: s.name }))]}
                    value={(edit.specialization_id ?? (edit as any).specialization?.id ?? 0) as any}
                    onChange={(v)=>setEdit({...edit!, specialization_id: v ? Number(v) : null})}
                  />
                </div>
              </div>
              <label>Описание</label>
              <input value={edit.norm_description || ''} onChange={e=>setEdit({...edit!, norm_description:e.target.value})} />
            </>
          )}

          {edit.norm_type === 'numeric' ? (
            <div className="row" style={{marginTop: 12}}>
              <div>
                <label>Норма: мин</label>
                <input type="number" value={edit.numeric_min ?? ''} onChange={e=>setEdit({...edit!, numeric_min:e.target.value===''? null : Number(e.target.value)})} />
              </div>
              <div>
                <label>Норма: макс</label>
                <input type="number" value={edit.numeric_max ?? ''} onChange={e=>setEdit({...edit!, numeric_max:e.target.value===''? null : Number(e.target.value)})} />
              </div>
              <div>
                <label>Серьёзное: ≤</label>
                <input type="number" value={edit.serious_lte ?? ''} onChange={e=>setEdit({...edit!, serious_lte:e.target.value===''? null : Number(e.target.value)})} />
              </div>
              <div>
                <label>Серьёзное: ≥</label>
                <input type="number" value={edit.serious_gte ?? ''} onChange={e=>setEdit({...edit!, serious_gte:e.target.value===''? null : Number(e.target.value)})} />
              </div>
            </div>
          ) : (
            <div>

              {editNormId ? (
                <div>
                  <h4>Синонимы (текстовые значения)</h4>
                  <div className="row">
                    <div>
                      <label>{edit.norm_type === 'text' ? 'Ключевые слова нормы' : 'Текст'}</label>
                      <input value={newSynonym.value_text} onChange={e=>setNewSynonym({...newSynonym, value_text:e.target.value})} />
                    </div>
                    <div>
                      <label>{edit.norm_type === 'text' ? 'Ключевые слова отклонений' : 'Норм. код'}</label>
                      <input value={newSynonym.normalized_code} onChange={e=>setNewSynonym({...newSynonym, normalized_code:e.target.value})} />
                    </div>
                    <div>
                      <label>Допустимо</label>
                      <select value={String(newSynonym.is_allowed)} onChange={e=>setNewSynonym({...newSynonym, is_allowed: e.target.value==='true'})}>
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                    </div>
                  </div>
                  <div style={{marginTop:12}}>
                    <button onClick={async ()=>{
                      const created = await Api.createTextRule({ analyses_norm_id: editNormId!, ...newSynonym });
                      const updated = await Api.listTextRulesByNorm(editNormId!);
                      setSynonyms(updated as any);
                      setNewSynonym({ value_text:'', normalized_code:'', is_allowed:true });
                      if (form.analysis_id && form.analysis_id > 0) {
                        const full = await Api.getAnalysesFullById(form.analysis_id);
                        if (full) {
                          setAnalysisFull(full);
                        }
                      }
                    }}>Добавить синоним</button>
                  </div>
                  <table className="table" style={{marginTop:12}}>
                    <thead>
                      <tr><th>ID</th><th>{edit.norm_type === 'text' ? 'Ключевые слова нормы' : 'Текст'}</th><th>{edit.norm_type === 'text' ? 'Ключевые слова отклонений' : 'Код'}</th><th>Допустимо</th><th></th></tr>
                    </thead>
                    <tbody>
                      {synonyms.length === 0 && (
                        <tr>
                          <td colSpan={5} style={{textAlign: 'center', color: 'var(--muted)'}}>
                            Нет синонимов
                          </td>
                        </tr>
                      )}
                      {synonyms.map((r, idx) => {
                        const ruleId = r.id ?? idx;
                        const isEditing = r.id && editingSynonymId === r.id;
                        
                        return isEditing && editingSynonym ? (
                          <tr key={ruleId}>
                            <td>{r.id ?? '—'}</td>
                            <td>
                              <input 
                                value={editingSynonym.value_text} 
                                onChange={e=>setEditingSynonym({...editingSynonym, value_text:e.target.value})}
                                style={{width: '100%'}}
                              />
                            </td>
                            <td>
                              <input 
                                value={editingSynonym.normalized_code} 
                                onChange={e=>setEditingSynonym({...editingSynonym, normalized_code:e.target.value})}
                                style={{width: '100%'}}
                              />
                            </td>
                            <td>
                              <select 
                                value={String(editingSynonym.is_allowed)} 
                                onChange={e=>setEditingSynonym({...editingSynonym, is_allowed:e.target.value==='true'})}
                                style={{width: '100%'}}
                              >
                                <option value="true">true</option>
                                <option value="false">false</option>
                              </select>
                            </td>
                            <td>
                              <div style={{display:'flex', gap:4}}>
                                <button 
                                  type="button" 
                                  className="secondary" 
                                  style={{padding: '4px 8px', fontSize: '0.85em'}}
                                  onClick={async ()=>{
                                    await Api.updateTextRule(editingSynonym.id, editingSynonym);
                                    const updated = await Api.listTextRulesByNorm(editNormId!);
                                    setSynonyms(updated as any);
                                    setEditingSynonymId(null);
                                    setEditingSynonym(null);
                                    if (form.analysis_id && form.analysis_id > 0) {
                                      const full = await Api.getAnalysesFullById(form.analysis_id);
                                      if (full) {
                                        setAnalysisFull(full);
                                      }
                                    }
                                  }}
                                >
                                  Сохранить
                                </button>
                                <button 
                                  type="button" 
                                  className="secondary" 
                                  style={{padding: '4px 8px', fontSize: '0.85em'}}
                                  onClick={()=>{
                                    setEditingSynonymId(null);
                                    setEditingSynonym(null);
                                  }}
                                >
                                  Отмена
                                </button>
                              </div>
                            </td>
                          </tr>
                        ) : (
                          <tr key={ruleId}>
                            <td>{r.id ?? '—'}</td>
                          <td>{r.value_text}</td>
                          <td>{r.normalized_code}</td>
                          <td>{String(r.is_allowed)}</td>
                            <td>
                              {r.id && (
                                <div style={{display:'flex', gap:4}}>
                                  <button 
                                    type="button" 
                                    className="secondary" 
                                    style={{padding: '4px 8px', fontSize: '0.85em'}}
                                    onClick={async ()=>{
                                      if (r.id && editNormId) {
                                        setEditingSynonymId(r.id);
                                        try {
                                          const updatedList = await Api.listTextRulesByNorm(editNormId);
                                          const updatedSynonym = updatedList.find((item: any) => item.id === r.id);
                                          if (updatedSynonym) {
                                            setEditingSynonym({
                                              id: updatedSynonym.id,
                                              analyses_norm_id: updatedSynonym.analyses_norm_id,
                                              value_text: updatedSynonym.value_text,
                                              normalized_code: updatedSynonym.normalized_code,
                                              is_allowed: updatedSynonym.is_allowed ?? true
                                            });
                                            setSynonyms(updatedList as any);
                                          } else {
                                            setEditingSynonym({
                                              id: r.id,
                                              analyses_norm_id: r.analyses_norm_id,
                                              value_text: r.value_text,
                                              normalized_code: r.normalized_code,
                                              is_allowed: r.is_allowed ?? true
                                            });
                                          }
                                        } catch (error) {
                                          console.error('Ошибка при загрузке данных синонима:', error);
                                          setEditingSynonym({
                                            id: r.id,
                                            analyses_norm_id: r.analyses_norm_id,
                                            value_text: r.value_text,
                                            normalized_code: r.normalized_code,
                                            is_allowed: r.is_allowed ?? true
                                          });
                                        }
                                      }
                                    }}
                                  >
                                    Изменить
                                  </button>
                                  <button 
                                    type="button" 
                                    className="secondary" 
                                    style={{padding: '4px 8px', fontSize: '0.85em', background: '#dc2626'}}
                                    onClick={()=>{
                                      if (r.id) {
                                        setConfirmDeleteSynonymId(r.id);
                                      }
                                    }}
                                  >
                                    Удалить
                                  </button>
                                </div>
                              )}
                            </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="muted">Чтобы добавить синонимы, сначала сохраните параметр или выберите его из списка.</p>
              )}
            </div>
          )}
          <div style={{marginTop:12, display:'flex', gap:8, alignItems:'center'}}>
            <div style={{display:'flex', gap:8}}>
              <button onClick={async ()=>{ 
                const editId = (edit as any)?.id;
                if (editId != null && editId !== undefined) {
                  const analysisId = edit.analysis_id ?? form.analysis_id;
                  if (!analysisId || analysisId === 0) {
                    console.warn('Не удалось сохранить: analysis_id не определен', { edit, form });
                    alert('Ошибка: не удалось определить ID анализа. Пожалуйста, выберите анализ в форме.');
                    return;
                  }
                  
                  const updateData: AnalysesNorms = {
                    id: editId,
                    analysis_id: analysisId,
                    group_id: edit.group_id ?? (edit as any).group?.id ?? null,
                    unit_id: edit.unit_id ?? (edit as any).unit?.id ?? null,
                    context_id: edit.context_id ?? (edit as any).context?.id ?? null,
                    specialization_id: edit.specialization_id ?? (edit as any).specialization?.id ?? null,
                    norm_name: edit.norm_name,
                    norm_code: edit.norm_code,
                    norm_description: edit.norm_description,
                    norm_type: edit.norm_type,
                    numeric_min: edit.numeric_min,
                    numeric_max: edit.numeric_max,
                    serious_lte: edit.serious_lte,
                    serious_gte: edit.serious_gte,
                    norm_unit: edit.norm_unit ?? (edit as any).unit?.unit_name ?? null,
                    version: (edit as any).version,
                    is_active: (edit as any).is_active ?? true,
                  };
                  await Api.updateNorm(editId as number, updateData);
                  if (form.analysis_id && form.analysis_id > 0) {
                    const full = await Api.getAnalysesFullById(form.analysis_id);
                    if (full) {
                      setAnalysisFull(full);
                    }
                  }
                  setEdit(null); 
                } else {
                  console.warn('Не удалось сохранить: id нормы не определен', edit);
                }
              }}>
                Сохранить
              </button>
              <button className="secondary" onClick={()=>setEdit(null)}>Отмена</button>
            </div>
            <button style={{background:'#dc2626', marginLeft:'auto'}} onClick={()=>setConfirmDeleteOpen(true)}>Удалить</button>
          </div>
        </Modal>
      )}

      {edit && (
        <ConfirmDialog
          open={confirmDeleteOpen}
          title="Удаление параметра"
          message={`Вы уверены что хотите удалить параметр для анализа "${analyses.find(a=>a.id===edit.analysis_id)?.analyses_name || ''}"?`}
          confirmText="Удалить"
          onClose={()=>setConfirmDeleteOpen(false)}
          onConfirm={async ()=>{
            const idToDelete = (edit as any)?.id as number | undefined;
            if (!idToDelete) { 
              console.warn('Не удалось удалить: id параметра не определен', edit);
              setConfirmDeleteOpen(false); 
              return; 
            }
            await Api.deleteNorm(idToDelete);
            if (form.analysis_id && form.analysis_id > 0) {
              const full = await Api.getAnalysesFullById(form.analysis_id);
              if (full) {
                setAnalysisFull(full);
              }
            }
            setConfirmDeleteOpen(false);
            setEdit(null);
          }}
        />
      )}

      {confirmDeleteSynonymId && (
        <Modal 
          title="Подтверждение удаления" 
          onClose={()=>setConfirmDeleteSynonymId(null)}
        >
          <p>Вы уверены, что хотите удалить этот синоним?</p>
          <div style={{marginTop: 12, display: 'flex', gap: 8}}>
            <button 
              style={{background: '#dc2626'}}
              onClick={async ()=>{
                if (confirmDeleteSynonymId) {
                  await Api.deleteTextRule(confirmDeleteSynonymId);
                  const updated = await Api.listTextRulesByNorm(editNormId!);
                  setSynonyms(updated as any);
                  setConfirmDeleteSynonymId(null);
                  if (form.analysis_id && form.analysis_id > 0) {
                    const full = await Api.getAnalysesFullById(form.analysis_id);
                    if (full) {
                      setAnalysisFull(full);
                    }
                  }
                }
              }}
            >
              Удалить
            </button>
            <button 
              className="secondary"
              onClick={()=>setConfirmDeleteSynonymId(null)}
            >
              Отмена
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}


